<template>
    <component class="address-link ui-looong"
        v-on:click.native.stop
        v-bind:is="component"
        v-bind:class="{ clickable: !isDisabled, muted: !address, 'full-name': hasDisplayName }"
        v-bind:to="routerParams"
        v-bind:data-loopa="name.substr(0, 42)"
        v-bind:data-poopa="name.substr(42)">

        <ui-copy-button class="ui-address__copy" lucide
            v-if="address && !hideCopy"
            v-on:click.native.prevent.stop
            v-bind:copy="address"/>
    </component>
</template>

<script>
// Contract types (contractHashes.json, generateMessage) and the pages showing
// them; anything else, wallets included, opens the address page:
const ROUTE_BY_TYPE = Object.freeze({
    nominator_pool: 'pool',
    single_nominator_v1_r0: 'pool',
    single_nominator_v1_r1: 'pool',
    liquid_staking_pool: 'pool',
    dedust_pool: 'lp',
    stonfi_pool: 'lp',
    stonfi_pool_v2: 'lp',
    jetton_master: 'token',
    jetton_wallet: 'token-wallet',
    nft_item: 'nft',
    nft_collection: 'collection',
    multisig_wallet_v2: 'multisig',
});

export default {
    props: {
        address: {
            required: false,
        },

        // address contract type (pool, nft, etc):
        type: String,

        // ton or t.me domain:
        alias: String,

        // Whether the address is clickable:
        disabled: {
            type: Boolean,
            default: false,
        },

        // Whether to show human readable name:
        hideName: {
            type: Boolean,
            default: false,
        },

        hideCopy: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        isDisabled() {
            return this.disabled || !this.address;
        },

        component() {
            return this.isDisabled ? 'span' : 'ui-link';
        },

        routerParams() {
            return {
                name: ROUTE_BY_TYPE[this.type] || 'address',
                params: { address: this.address },
            };
        },

        name() {
            if (!this.address) {
                return this.$t('common.empty');
            }

            if (this.hideName) {
                return this.address;
            }

            const addressBookName = this.$store.getters.getLocalAddressbookName(this.address) || this.$store.getters.getAddressName(this.address);

            // address book returns full address if there are no meta records for that address:
            if (addressBookName !== this.address) {
                return addressBookName;
            }

            // limiting alias max length to fit the table (TODO):
            const hasValidAlias = this.alias && this.alias.length <= 36;

            return hasValidAlias
                ? this.alias
                : this.address;
        },

        hasDisplayName() {
            // Display usernames and domains full
            const isMaxLength = this.name.length <= 22;
            const notSameAsAddress = this.name !== this.address;

            return !!(
                this.address
                && !this.hideName
                && this.name
                && isMaxLength
                && notSameAsAddress
            );
        },
    },
};
</script>

<style lang="scss">
.ui-looong {
    position: relative;
    white-space: nowrap;
    // Same features as body, minus calt: Inter turns "9x6" into "9×6".
    font-feature-settings: 'tnum' 1, 'cv05' 1, 'calt' 0;
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    max-width: 100%;
    min-width: 0;
    &::before {
        flex-grow: 0;
        content: attr(data-loopa);
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &::after {
        flex-shrink: 0;
        content: attr(data-poopa);
    }
    &.full-name {
        white-space: nowrap;
        flex: 0 0 auto;
        align-items: center;
    }
}

.address-link {
    text-decoration: none;
    border-bottom: 1px solid transparent;
    margin-bottom: -1px;

    // direct disabled child of <router-link> or <a> = clickable
    &.clickable, a > & {
        color: var(--blue-bright);

        // the underline is drawn by ::before/::after so that it tracks the
        // address text only and never runs under the copy button:
        &:hover {
            text-decoration: none;
        }

        // Hover effects are mouse-only on purpose: on touch devices Safari
        // treats the first tap as a hover, swallows the click when that hover
        // repaints the element, and only the second tap follows the link.
        // On the mobile layout the highlight is a filled box (see app.scss),
        // so the underline would only double up with it:
        @media (hover: hover) and (min-width: 481px) {
            &:hover::before,
            &:hover::after {
                text-decoration: underline;
                text-underline-offset: 2px;
            }
        }
    }

    &.muted {
        opacity: .65;
        pointer-events: none;
    }

    &:hover {
        text-decoration: none;
    }
}

// Mirrors the `a > &` selector above: when the address is the content of a
// parent <a>, that anchor must not draw its own underline. It would be painted
// across the whole (atomic inline) address box — under the copy button and at a
// different offset than the ::before/::after underline, i.e. a second line.
a:has(> .address-link):hover {
    text-decoration: none;
}

.ui-address__copy.ui-copy-button {
    display: none;
    position: absolute;
    top: -1px;
    right: 0;
    bottom: -1px;
    align-items: center;
    justify-content: flex-end;
    padding-left: 28px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent 0%, var(--card-background) 80%);
    .ui-copy-button__icon {
        pointer-events: auto;
        cursor: pointer;
        margin: 0;
        padding: 2px;
    }
}

@media (hover: hover) {
    .address-link:hover .ui-address__copy {
        display: inline-flex;
    }
}

</style>
